import React from "react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

export type ModuleTrailCrumb = {
  label: string;
  onClick?: () => void;
};

/** Mismo lenguaje visual que Opciones: botón muted, hover foreground. */
export function CrumbLink({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick: () => void;
}) {
  return (
    <BreadcrumbLink asChild>
      <button
        type="button"
        onClick={onClick}
        className="cursor-pointer text-muted-foreground hover:text-foreground"
      >
        {children}
      </button>
    </BreadcrumbLink>
  );
}

export function CrumbSep() {
  return (
    <BreadcrumbSeparator className="[&>svg]:hidden">
      <span aria-hidden="true">&gt;</span>
    </BreadcrumbSeparator>
  );
}

/**
 * Trail de módulo (Opciones, Proveedores, Clientes, …).
 * El último crumb es la página actual. Los anteriores con onClick son links.
 */
export function ModuleTrail({ crumbs }: { crumbs: ModuleTrailCrumb[] }) {
  if (crumbs.length === 0) return null;
  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="text-xs sm:gap-1.5">
        {crumbs.map((c, i) => {
          const isLast = i === crumbs.length - 1;
          return (
            <React.Fragment key={`${c.label}-${i}`}>
              {i > 0 ? <CrumbSep /> : null}
              <BreadcrumbItem>
                {isLast ? (
                  <BreadcrumbPage>{c.label}</BreadcrumbPage>
                ) : c.onClick ? (
                  <CrumbLink onClick={c.onClick}>{c.label}</CrumbLink>
                ) : (
                  <span className="text-muted-foreground">{c.label}</span>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * Título de página (H1/H2) con el trail Opciones debajo. No reemplaza el título.
 * Con un solo crumb no se muestra el trail (evita duplicar el H1).
 */
export function ModuleTitleBlock({
  title,
  crumbs,
  as: Tag = "h2",
  titleClassName = "text-2xl font-bold leading-tight",
}: {
  title: string;
  crumbs: ModuleTrailCrumb[];
  as?: "h1" | "h2";
  titleClassName?: string;
}) {
  return (
    <div className="min-w-0">
      <Tag className={titleClassName}>{title}</Tag>
      {crumbs.length > 1 ? (
        <div className="mt-0.5">
          <ModuleTrail crumbs={crumbs} />
        </div>
      ) : null}
    </div>
  );
}
